import type { ReactNode } from 'react';
import { cn } from '../lib/utils.js';

/**
 * Semantic tone of a status. Deliberately domain-free: `packages/ui` must not know
 * about certificates, policies or any other product taxonomy. Each product maps its
 * own statuses onto these tones — see the certificate portal's `cert-status.ts`.
 *
 * Tones are reserved for state. Never reuse a chart series colour here, and never use
 * a tone as a series colour: a reader must be able to tell "this is bad" from "this is
 * series 4" without a legend.
 */
export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const TONE_TOKEN: Record<StatusTone, string> = {
  neutral: 'var(--color-muted-foreground)',
  info: 'var(--color-info)',
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  danger: 'var(--color-destructive)',
};

export interface StatusPillProps {
  tone?: StatusTone;
  children: ReactNode;
  className?: string;
}

export function StatusPill({ tone = 'neutral', children, className }: StatusPillProps) {
  const token = TONE_TOKEN[tone];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
        className,
      )}
      style={{
        // Mixing toward the surface/foreground tokens rather than hardcoding a tint keeps
        // the pill legible in both themes: the wash follows the surface and the text
        // follows the ink, so nothing needs a separate dark-mode value.
        background: `color-mix(in oklab, ${token} 16%, var(--color-surface))`,
        color: `color-mix(in oklab, ${token} 72%, var(--color-foreground))`,
      }}
    >
      <span
        className="inline-block h-[5px] w-[5px] shrink-0 rounded-full"
        style={{ background: token }}
      />
      {children}
    </span>
  );
}
